import { IUserResponse } from '@kcftech/prometheus-client';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { DateTimeDisplay } from 'components/date_time/DateTimeDisplay';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { NoValue } from 'components/no_value/NoValue';
import { UsersPagedDataSource } from 'custom_hooks/data_hooks/data_source/usersDataSource';
import { RemoveUserCell } from 'pages/Settings/Users/table_cells/RemoveUserCell';
import { UserApprovedCell } from 'pages/Settings/Users/table_cells/UserApprovedCell';
import { UserEmailCell } from 'pages/Settings/Users/table_cells/UserEmailCell';
import { UserNameCell } from 'pages/Settings/Users/table_cells/UserNameCell';
import { UserRoleCell } from 'pages/Settings/Users/table_cells/UserRoleCell';
import { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { getId } from 'utils/data';
import { formatPhoneNumber } from 'utils/phonenumber';
import { getSettingsBaseRoute } from 'utils/RouteHelper';

export type UsersTableProps = Readonly<{
    /**
     * If supplied, shows corporation specific role code
     */
    corporationId?: string;
    /**
     * If supplied, shows location specific role code
     */
    locationId?: string;
    dataSource: UsersPagedDataSource;
}>;

export type UsersTableCellProps = Readonly<{
    rowData: IUserResponse;
    onChange: () => void;
}>;

export type CorporationSpecificUserCellProps = UsersTableCellProps &
    Readonly<{
        corporationId: string;
    }>;

export type LocationSpecificUserCellProps = UsersTableCellProps &
    Readonly<{
        locationId: string;
    }>;

const getUserRoleColumnHeader = (
    corporationId: string | undefined,
    locationId: string | undefined,
) => {
    if (locationId) {
        return 'Location Role';
    }

    if (corporationId) {
        return 'Corporation Role';
    }

    return 'Application Role';
};

export function UsersTable({ corporationId, locationId, dataSource }: UsersTableProps) {
    const { url } = useRouteMatch();
    const settingsRoute = getSettingsBaseRoute(url);
    const { reloadPage } = dataSource;

    const columns = useMemo<TableColumn<IUserResponse>[]>(
        () => [
            {
                id: 'FullName',
                header: () => 'Full Name',
                value: ({ rowData }) => (
                    <UserNameCell
                        rowData={rowData}
                        onChange={reloadPage}
                        url={`${settingsRoute}/users/${rowData.Id}`}
                    />
                ),
                primary: true,
                sortOn: ['FirstName', 'LastName'],
            },
            {
                id: 'UserRole',
                header: () => getUserRoleColumnHeader(corporationId, locationId),
                value: ({ rowData }) => (
                    <UserRoleCell
                        corporationId={corporationId}
                        locationId={locationId}
                        rowData={rowData}
                        onChange={reloadPage}
                    />
                ),
                minWidth: '15rem',
            },
            {
                id: 'Email',
                header: () => 'Email Address',
                value: ({ rowData }) => (
                    <UserEmailCell rowData={rowData} onChange={reloadPage} />
                ),
                sortOn: 'Email',
            },
            {
                id: 'PhoneNumber',
                header: () => 'Phone',
                value: ({ rowData }) =>
                    rowData.PhoneNumber ? (
                        formatPhoneNumber(rowData.PhoneNumber)
                    ) : (
                        <NoValue />
                    ),
                minWidth: '15rem',
            },
            {
                id: 'LastActive',
                header: () => 'Last Active',
                value: ({ rowData: { LastRequestTime } }) => (
                    <DateTimeDisplay
                        date={LastRequestTime}
                        fallback={<span>Never</span>}
                    />
                ),
                sortOn: 'LastRequestTime',
            },
            {
                id: 'IsApproved',
                header: () => 'Approved',
                value: ({ rowData }) => (
                    <UserApprovedCell rowData={rowData} onChange={reloadPage} />
                ),
                sortOn: 'IsApproved',
            },
            {
                id: 'RemoveUser',
                header: () => 'Actions',
                enabled: locationId !== undefined || corporationId !== undefined,
                value: ({ rowData }) => (
                    <ActionMenuTableCell>
                        <RemoveUserCell
                            rowData={rowData}
                            onChange={reloadPage}
                            locationId={locationId}
                            corporationId={corporationId}
                        />
                    </ActionMenuTableCell>
                ),
            },
        ],
        [locationId, corporationId, reloadPage, settingsRoute],
    );

    return (
        <DataSourceTable
            dataSource={dataSource}
            noResultMessage="No users found"
            columns={columns}
            rowKey={getId}
        />
    );
}
